import { useMemo, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Country, State, City } from "country-state-city";
import { NeuSelect, NeuInput } from "../neu/Neu";
import { detectLocation } from "../../lib/geolocate";

// منتقي جغرافي عالمي: زر GPS اختياري + دولة ← منطقة ← مدينة (نص ذكي لندرة بيانات المدن).
export const GeoCascade = ({ value = {}, onChange, prefix = "geo", gps = false, onAddress }) => {
  const [locating, setLocating] = useState(false);
  const countries = useMemo(() => Country.getAllCountries(), []);
  const country = countries.find((c) => c.name === value.country);
  const states = useMemo(() => (country ? State.getStatesOfCountry(country.isoCode) : []), [country]);
  const state = states.find((s) => s.name === value.region);
  const cities = useMemo(
    () => (country && state ? City.getCitiesOfState(country.isoCode, state.isoCode) : []),
    [country, state],
  );
  const locate = async () => {
    setLocating(true);
    try {
      const g = await detectLocation();
      onChange({ country: g.country, region: g.region, city: g.city, lat: g.lat, lng: g.lng });
      if (onAddress && g.address) onAddress(g.address);
      toast.success("تم تحديد موقع الشركة بدقة");
    } catch {
      toast.error("تعذّر تحديد الموقع — فعّل السماح بالوصول للموقع في المتصفح");
    } finally {
      setLocating(false);
    }
  };
  const ll = (o) => (o && o.latitude ? { lat: +o.latitude, lng: +o.longitude } : {});
  const pickCountry = (name) => {
    const c = countries.find((x) => x.name === name);
    onChange({ country: name, region: "", city: "", lat: null, lng: null, ...ll(c) });
  };
  const pickRegion = (name) => {
    const s = states.find((x) => x.name === name);
    onChange({ region: name, city: "", ...ll(s.latitude ? s : country) });
  };
  const pickCity = (name) => {
    const c = cities.find((x) => x.name.toLowerCase() === name.toLowerCase());
    onChange(c ? { city: name, ...ll(c) } : { city: name });
  };
  return (
    <div className="grid gap-3">
      {gps ? (
        <button type="button" data-testid={`${prefix}-gps`} onClick={locate} disabled={locating}
          className="flex items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground shadow-neu-sm transition hover:brightness-110 disabled:opacity-60">
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          تحديد موقع الشركة تلقائياً (GPS)
        </button>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-3">
        <NeuSelect data-testid={`${prefix}-country`} value={value.country || ""}
          onChange={(e) => pickCountry(e.target.value)}>
          <option value="">— الدولة —</option>
          {countries.map((c) => <option key={c.isoCode} value={c.name}>{c.flag} {c.name}</option>)}
        </NeuSelect>
        <NeuSelect data-testid={`${prefix}-region`} value={value.region || ""} disabled={!country}
          onChange={(e) => pickRegion(e.target.value)}>
          <option value="">— المنطقة/المحافظة —</option>
          {states.map((s) => <option key={s.isoCode} value={s.name}>{s.name}</option>)}
          {value.region && !state ? <option value={value.region}>{value.region}</option> : null}
        </NeuSelect>
        <div>
          <NeuInput data-testid={`${prefix}-city`} list={`${prefix}-city-list`} placeholder="المدينة"
            className="!h-12" value={value.city || ""} disabled={!country}
            onChange={(e) => pickCity(e.target.value)} />
          <datalist id={`${prefix}-city-list`}>
            {cities.map((c) => <option key={c.name} value={c.name} />)}
          </datalist>
        </div>
      </div>
      {value.lat ? (
        <p className="text-[11px] font-mono text-muted-foreground" dir="ltr" data-testid={`${prefix}-coords`}>
          📍 {Number(value.lat).toFixed(5)}, {Number(value.lng).toFixed(5)}
        </p>
      ) : null}
    </div>
  );
};
